/**
 * USVI Pirate's Guild — Canonical taxonomy for business listings.
 *
 * Centralized so the directory filter, listing editor form, and any future
 * map/event surfaces all reference the same enums. Migration script in
 * `scripts/migrate-listing-categories.js` (Phase 4 follow-up) will normalize
 * existing free-form `category` strings to these canonical values.
 *
 * Backward compat: legacy free-form categories are preserved as `alias`
 * arrays so the filter UI can still match historical data while we migrate.
 */

export const CATEGORY_KEYS = Object.freeze({
  RESTAURANTS: "restaurants",
  BARS: "bars",
  SHOPS: "shops",
  TOURS: "tours",
  HOTELS: "hotels",
  CAR_RENTALS: "car-rentals",
  EVENTS: "events",
  BEACH_VENDORS: "beach-vendors",
  TRANSPORTATION: "transportation",
  LOCAL_SERVICES: "local-services",
});

export const CATEGORIES = Object.freeze([
  { key: CATEGORY_KEYS.RESTAURANTS,    label: "Restaurants",    aliases: ["restaurant", "food", "dining", "eatery", "cafe", "café"] },
  { key: CATEGORY_KEYS.BARS,           label: "Bars",           aliases: ["bar", "pub", "lounge", "nightlife", "tavern"] },
  { key: CATEGORY_KEYS.SHOPS,          label: "Shops",          aliases: ["shop", "shopping", "retail", "store", "boutique", "gift"] },
  { key: CATEGORY_KEYS.TOURS,          label: "Tours",          aliases: ["tour", "tour operator", "excursion", "snorkel", "dive", "charter"] },
  { key: CATEGORY_KEYS.HOTELS,         label: "Hotels",         aliases: ["hotel", "lodging", "resort", "inn", "villa", "rental"] },
  { key: CATEGORY_KEYS.CAR_RENTALS,    label: "Car Rentals",    aliases: ["car rental", "car-rental", "rental car", "auto"] },
  { key: CATEGORY_KEYS.EVENTS,         label: "Events",         aliases: ["event", "festival", "concert", "meetup"] },
  { key: CATEGORY_KEYS.BEACH_VENDORS,  label: "Beach Vendors",  aliases: ["beach", "beach vendor", "watersports", "beach rental"] },
  { key: CATEGORY_KEYS.TRANSPORTATION, label: "Transportation", aliases: ["transport", "taxi", "ferry", "shuttle", "transit"] },
  { key: CATEGORY_KEYS.LOCAL_SERVICES, label: "Local Services", aliases: ["services", "service", "wellness", "spa", "fitness"] },
]);

/**
 * Top-level business type — mirrors the `BusinessType` union in
 * src/types/firestore.ts. Distinct from CATEGORIES (sub-categorization).
 */
export const BUSINESS_TYPES = Object.freeze([
  { key: "restaurant",    label: "Restaurant" },
  { key: "bar",           label: "Bar / Nightlife" },
  { key: "hotel",         label: "Hotel / Lodging" },
  { key: "charter",       label: "Charter" },
  { key: "tour",          label: "Tour Operator" },
  { key: "retail",        label: "Retail / Shop" },
  { key: "service",       label: "Service" },
  { key: "entertainment", label: "Entertainment" },
  { key: "other",         label: "Other" },
]);

export const ISLANDS = Object.freeze([
  { key: "stt", label: "St. Thomas",   aliases: ["st thomas", "st. thomas", "saint thomas", "stt"] },
  { key: "stj", label: "St. John",     aliases: ["st john", "st. john", "saint john", "stj"] },
  { key: "stx", label: "St. Croix",    aliases: ["st croix", "st. croix", "saint croix", "stx"] },
  { key: "wj",  label: "Water Island", aliases: ["water island"] },
]);

export const LISTING_TAGS = Object.freeze([
  { key: "family-friendly", label: "Family Friendly" },
  { key: "nightlife",       label: "Nightlife" },
  { key: "food-and-drink",  label: "Food & Drink" },
  { key: "outdoors",        label: "Outdoors" },
  { key: "waterfront",      label: "Waterfront" },
  { key: "live-music",      label: "Live Music" },
  { key: "pet-friendly",    label: "Pet Friendly" },
  { key: "accessible",      label: "Accessibility-Friendly" },
  { key: "local-favorite",  label: "Local Favorite" },
  { key: "hidden-gem",      label: "Hidden Gem" },
]);

/**
 * Pass tiers that a listing's offer is eligible for. Mirrors the keys in
 * src/constants/plans.js (TRAVELER_TIERS) but stored as a flat allow-list on
 * the listing document.
 */
export const PASS_TIER_KEYS = Object.freeze({
  DECKHAND: "deckhand",
  ADVENTURER_7DAY: "adventurer-7day",
  ADVENTURER_ANNUAL: "adventurer-annual",
  TRAVELER_PLUS: "traveler-plus",
});

export const PASS_TIER_LABELS = Object.freeze({
  [PASS_TIER_KEYS.DECKHAND]:          "Deckhand (Free)",
  [PASS_TIER_KEYS.ADVENTURER_7DAY]:   "7-Day Adventurer",
  [PASS_TIER_KEYS.ADVENTURER_ANNUAL]: "Annual Adventurer",
  [PASS_TIER_KEYS.TRAVELER_PLUS]:     "Traveler Plus",
});

/**
 * All passes (used as default when a listing is open to everyone).
 */
export const ALL_PASS_TIERS = Object.freeze(Object.values(PASS_TIER_KEYS));

/**
 * Resolve a free-form / legacy category string to a canonical CATEGORY_KEYS
 * value. Returns the canonical key, or null when no alias matches.
 *
 * @param {string} raw - listing.category or listing.type from Firestore
 * @returns {string|null}
 */
export function resolveCategoryKey(raw) {
  if (!raw || typeof raw !== "string") return null;
  const lower = raw.trim().toLowerCase();
  if (!lower) return null;

  // Direct key match first
  const direct = CATEGORIES.find((c) => c.key === lower);
  if (direct) return direct.key;

  // Alias match (substring on alias OR vice-versa to be forgiving)
  for (const c of CATEGORIES) {
    if (c.label.toLowerCase() === lower) return c.key;
    if (c.aliases.some((a) => lower === a || lower.includes(a) || a.includes(lower))) {
      return c.key;
    }
  }
  return null;
}

/**
 * Resolve a free-form location string to an island key, or null.
 *
 * @param {string} raw - listing.location or listing.island
 * @returns {string|null}
 */
export function resolveIslandKey(raw) {
  if (!raw || typeof raw !== "string") return null;
  const lower = raw.trim().toLowerCase();
  if (!lower) return null;

  // Direct key match
  const direct = ISLANDS.find((i) => i.key === lower);
  if (direct) return direct.key;

  // Alias match
  for (const island of ISLANDS) {
    if (island.label.toLowerCase() === lower) return island.key;
    if (island.aliases.some((a) => lower.includes(a))) return island.key;
  }
  return null;
}

export function getCategoryLabel(key) {
  const found = CATEGORIES.find((c) => c.key === key);
  return found ? found.label : key;
}

export function getIslandLabel(key) {
  const found = ISLANDS.find((i) => i.key === key);
  return found ? found.label : key;
}
